/**
 * What a trainer about to delete their account must be told about a renewal that outlives it.
 *
 * Ported from the mobile app's `src/features/profile/model/storeRenewalNotice.ts`, whose reasoning
 * carries over word for word.
 *
 * **It decides a NOTICE, never a gate.** Apple's own account-deletion guidance — the page Guideline
 * 5.1.1(v) links — says to *notify* a subscriber that billing continues and *request* that they
 * cancel, not to require it. A subscription hangs off the store account rather than off ours, so
 * deleting here can never stop it, and refusing to delete until it stops would strand the person
 * outside the app. No answer this returns can be read as „not allowed yet".
 *
 * **The read can fail, and silence is not an answer.** Not knowing whether anything is running is
 * indistinguishable from „you have no subscription", and a paying trainer would then delete their
 * account never having been told billing continues — the disclosure failing exactly when it is
 * least visible. So an unresolved read answers `pending` (the notice is coming, hold its place) or
 * `unknown` (say it with no store to open), never `none`.
 *
 * **A loaded entitlement outranks a failed refetch.** `isError` is also true when a background
 * refetch fails over cached data; branching on it first would replace a known storefront with a
 * vaguer message.
 *
 * **A renewal already switched off asks for nothing.** `autoRenew: false` means the one thing this
 * notice would ask for has been done, even though a storefront still holds the purchase — which is
 * exactly where this differs from `storeTargetOf`, whose question is „where is this managed".
 */

import type { TrainWithMeModulesSubscriptionsDtosResponsesGetEntitlementsResponse as EntitlementsDto } from '~/shared/api/generated/model';

import { storeTargetOf, type StoreTarget } from './subscriptionCard';

/** What the dialog shows beside the consequences. */
export type StoreRenewalNotice =
  /** Nothing is renewing anywhere we could point at — no notice at all. */
  | { readonly kind: 'none' }
  /** The entitlement has not landed: hold the notice's place rather than dropping it in late. */
  | { readonly kind: 'pending' }
  /** The read failed: say a subscription may keep renewing, with nothing to open. */
  | { readonly kind: 'unknown' }
  /** A renewal is running, and this storefront manages it. */
  | { readonly kind: 'store'; readonly target: StoreTarget };

/** The entitlement read as the dialog has it — the data, and whether it has resolved. */
export interface StoreRenewalRead {
  readonly entitlements: EntitlementsDto | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

export function storeRenewalNotice(read: StoreRenewalRead): StoreRenewalNotice {
  if (read.entitlements !== undefined) {
    const target =
      read.entitlements.autoRenew === true ? storeTargetOf(read.entitlements) : null;
    return target === null ? { kind: 'none' } : { kind: 'store', target };
  }

  if (read.isLoading) return { kind: 'pending' };
  if (read.isError) return { kind: 'unknown' };

  return { kind: 'none' };
}
